import { StaticImageData } from 'next/image';

export interface Vase {
  id: number;
  name: string;
  description: string;
  image: string;
  modelPath?: string;
  featured?: boolean;
  surfaceStyle?: 'smooth' | 'faceted';
  anisotropyMultiplier?: number;
  /** Default turntable yaw (degrees) — hero + product first frame and reset. */
  defaultYawDeg?: number;
}

export const vases: Vase[] = [
  {
    id: 1,
    name: 'Atargasan',
    description: "Inspired by Atargatis, the first mermaid goddess, this vase flows with myth, emotion, and oceanic grace.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Atargasan.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Atargasan.glb`,
    featured: true,
    surfaceStyle: 'faceted',
  },
  {
    id: 2,
    name: 'Avelseach',
    description: "Inspired by Avalon and the sacred number seven, this vase channels Celtic myth and spiritual harmony through spiraling symbols of ancient virtues.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Avelseach.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Avelseach.glb`,
  },
  {
    id: 3,
    name: 'Cehuiq',
    description: "Rooted in Mesoamerican myth, this nine-sided vase honours the sacred cycles of life, death, and cosmic rhythm from Maya and Aztec traditions.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Cehuiq.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Cehuiq.glb`,
  },
  {
    id: 4,
    name: 'Fujinasu',
    description: "Echoing the breath of Fujin, the Japanese wind god, this vase captures the graceful motion of air in a design that balances stillness and storm.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Fujinasu.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Fujinasu.glb`,
  },
  {
    id: 5,
    name: "Ma'atkheper",
    description: "A union of Egyptian gods Ma'at and Kheper, this vase symbolises truth, transformation, and the eternal rhythm of creation and rebirth.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Maatkheper.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Maatkheper.glb`,
  },
  {
    id: 6,
    name: "Njordbakn",
    description: "Channelling Njord's oceanic power and the shimmer of northern lights, this vase reflects Norse myth and the guiding light of sea-born journeys",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Njordbakn.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Njordbakn.glb`,
  },
  {
    id: 7,
    name: 'Simorakat',
    description: "Inspired by the Persian Simurgh, this faceted vase channels divine wisdom and protection, its form echoing the sacred feathers of a celestial guardian.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Simorakat.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Simorakat.glb`,
    surfaceStyle: 'faceted',
  },
  {
    id: 8,
    name: 'Svarogogon',
    description: "Born of Slavic myth, this radiant vase rises like a sun-forged star, honouring Svarog's creative fire and the cosmic forge that shapes all existence.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Svarogogon.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Svarogogon.glb`,
    surfaceStyle: 'faceted',
  },
  {
    id: 9,
    name: 'Tarnaqalignak',
    description: "A tribute to Alignak, Inuit god of the moon and tides, this vase captures the Arctic night's serene glow and the lunar rhythm of life.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Tarnaqalignak.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Tarnaqalignak.glb`,
  },
  {
    id: 10,
    name: "Tawhirimana",
    description: "Inspired by Tawhirimatea, Maori god of storms, this swirling form embodies the fierce love and cosmic wind that connect earth and sky.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Tawhirimana.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Tawhirimana.glb`,
  },
  {
    id: 11,
    name: 'Vishrakhna',
    description: "Echoing Vishnu's Sudarshana Chakra, this bold design unites divine strength and flowing grace in a symbol of cosmic balance and eternal motion.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Vishrakhna.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Vishrakhna.glb`,
  },
  {
    id: 12,
    name: "Winyan Cante",
    description: "Shaped like a sacred heart, this vase honours Lakota femininity and the Double Woman's duality—where creation and sorrow dance in eternal embrace",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Winyan Cante.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Winyan Cante.glb`,
  },
  {
    id: 13,
    name: 'Zephyra',
    description: "Graceful as a Grecian breeze, this fluid vase mirrors Zephyr's gentle wind, flowing like ancient drapery through myth and sky.",
    image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/images/public/products/Zephyra.png`,
    modelPath: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/models/Zephyra.glb`,
  }
];

export const findVaseByReference = (options: { vaseId?: number; modelPath?: string }) => {
  if (typeof options.vaseId === 'number') {
    const byId = vases.find((vase) => vase.id === options.vaseId);
    if (byId) return byId;
  }

  if (options.modelPath) {
    const byModelPath = vases.find((vase) => vase.modelPath === options.modelPath);
    if (byModelPath) return byModelPath;
  }

  return undefined;
};
